import React, { memo } from "react";
import { Text, View } from "react-native";
import { Image } from "expo-image";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { Shadows } from "@/constants/Shadows";
import { useBrandLogo } from "@/hooks/useBrandLogo";
import { UserFinanceDataResponse, AccountSummary } from "@/types/financeData";

interface BalancesCardProps {
  financeData: UserFinanceDataResponse | null;
}

// Helper to format currency from cents
const formatCurrency = (cents: number, showSign = false): string => {
  const dollars = cents / 100;
  const formatted = dollars.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  if (showSign && cents > 0) {
    return `+$${formatted}`;
  }
  if (showSign && cents < 0) {
    return `-$${Math.abs(dollars).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  }
  return `$${formatted}`;
};

// Get initial letter for fallback logo
const getInitialLetter = (name: string): string => {
  return name.charAt(0).toUpperCase();
};

// Balance item component
const BalanceItem = ({
  account,
  isLast,
}: {
  account: AccountSummary;
  isLast: boolean;
}) => {
  const { icon } = useBrandLogo(account.name);

  return (
    <View>
      <View style={styles.balanceRow}>
        {/* Logo */}
        <View style={styles.logoContainer}>
          {icon ? (
            <Image source={{ uri: icon }} style={styles.logo} />
          ) : (
            <View style={styles.logoFallback}>
              <Text style={styles.logoInitial}>
                {getInitialLetter(account.name)}
              </Text>
            </View>
          )}
        </View>

        {/* Name */}
        <View style={styles.balanceInfo}>
          <Text style={styles.itemName} numberOfLines={1}>
            {account.name}
          </Text>
          <Text style={styles.itemSubtext}>Yesterday</Text>
        </View>

        {/* Balance column */}
        <View style={styles.balanceColumn}>
          <Text style={styles.balanceAmount}>
            {formatCurrency(account.balance)}
          </Text>
        </View>

        {/* Change column */}
        <View style={styles.balanceColumn}>
          <Text
            style={
              account.monthlyChange >= 0
                ? styles.balanceChangePositive
                : styles.balanceChangeNegative
            }
          >
            {formatCurrency(account.monthlyChange, true)}
          </Text>
        </View>
      </View>

      {!isLast && <View style={styles.divider} />}
    </View>
  );
};

export default memo(function BalancesCard({ financeData }: BalancesCardProps) {
  const accounts = financeData?.accounts || [];

  if (!accounts.length) {
    return null;
  }

  return (
    <View style={styles.card}>
      {/* Header */}
      <View style={styles.cardHeader}>
        <Text style={styles.cardTitle}>Balances</Text>
        <Text style={styles.cardDescription}>
          Changes in your connected account balances.
        </Text>
      </View>

      <View style={styles.headerDivider} />

      {/* Column headers */}
      <View style={styles.columnHeaders}>
        <View style={styles.columnHeaderSpacer} />
        <View style={styles.balanceColumn}>
          <Text style={styles.columnHeaderText}>Balance</Text>
        </View>
        <View style={styles.balanceColumn}>
          <Text style={styles.columnHeaderText}>This month</Text>
        </View>
      </View>

      {/* Balance list */}
      <View style={styles.itemsList}>
        {accounts.map((account, index) => (
          <BalanceItem
            key={account.accountId + index}
            account={account}
            isLast={index === accounts.length - 1}
          />
        ))}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingTop: 20,
    paddingBottom: 8,
    ...Shadows.card,
  },
  cardHeader: {
    gap: 4,
    paddingBottom: 8,
  },
  cardTitle: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
    lineHeight: 23,
  },
  cardDescription: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
    lineHeight: 16,
  },
  headerDivider: {
    height: 1,
    backgroundColor: "#EBEBEB",
    marginBottom: 8,
  },
  columnHeaders: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 4,
  },
  columnHeaderSpacer: {
    flex: 1,
    marginLeft: 52, // 40px logo + 12px gap
  },
  balanceColumn: {
    width: 80,
    alignItems: "flex-end",
  },
  columnHeaderText: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#A3A3A3",
    lineHeight: 27,
  },
  itemsList: {
    gap: 0,
  },
  balanceRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    gap: 12,
  },
  logoContainer: {
    width: 40,
    height: 40,
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  logoFallback: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#F3F4F6",
    alignItems: "center",
    justifyContent: "center",
  },
  logoInitial: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  balanceInfo: {
    flex: 1,
    gap: 2,
  },
  itemName: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    lineHeight: 27,
  },
  itemSubtext: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    opacity: 0.5,
    lineHeight: 23,
  },
  balanceAmount: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    lineHeight: 27,
    textAlign: "right",
  },
  balanceChangePositive: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#0F9129",
    lineHeight: 27,
    textAlign: "right",
  },
  balanceChangeNegative: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#C12915",
    lineHeight: 27,
    textAlign: "right",
  },
  divider: {
    height: 1,
    backgroundColor: "#F3F3F3",
  },
});
